import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import documentService from '../services/DocumentService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function ChunkListDialog({ open, onClose, kbaseId, documentId, documentName }) {
    const { t } = useTranslation();

    const [chunks, setChunks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const fetchChunks = useCallback(async () => {
        if (!kbaseId || !documentId) return;
        try {
            setLoading(true);
            const data = await documentService.getDocumentChunks(kbaseId, documentId);
            setChunks(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching chunks', e);
            setSnackbar({ open: true, message: e?.message || t('chunks.fetchError', 'Failed to load chunks'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [kbaseId, documentId, t]);

    useEffect(() => {
        if (open) {
            fetchChunks();
        }
    }, [open, fetchChunks]);

    const rows = useMemo(() => {
        const list = Array.isArray(chunks) ? chunks : [];
        const q = normalizeString(filter).toLowerCase();
        if (!q) return list;
        return list.filter((c) => {
            const text = String(c?.text || '').toLowerCase();
            const meta = (() => {
                try {
                    return JSON.stringify(c?.metadata ?? '').toLowerCase();
                } catch {
                    return String(c?.metadata ?? '').toLowerCase();
                }
            })();
            return text.includes(q) || meta.includes(q);
        });
    }, [chunks, filter]);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {t('chunks.title', 'Chunks')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {documentName ? documentName : documentId ? `Document: ${documentId}` : ''}
                        </Typography>
                    </Box>
                    <Tooltip title={t('chunks.refresh', 'Refresh')} placement="top" arrow>
                        <span>
                            <IconButton size="small" onClick={fetchChunks} disabled={loading || !kbaseId || !documentId}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            label={t('chunks.search', 'Search')}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            sx={{ minWidth: 260 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {t('chunks.count', 'Count')}: {rows.length}
                        </Typography>
                    </Box>

                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                        {t('chunks.text', 'Text')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200', width: 260 }}>
                                        {t('chunks.metadata', 'Metadata')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={String(row.chunkId)} hover>
                                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.text || '-'}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                    fontSize: 12,
                                                }}
                                            >
                                                {row.metadata
                                                    ? (() => {
                                                          try {
                                                              return JSON.stringify(row.metadata, null, 2);
                                                          } catch {
                                                              return String(row.metadata);
                                                          }
                                                      })()
                                                    : '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            {t('chunks.empty', 'No chunks found.')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                        {t('chunks.close', 'Close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default ChunkListDialog;
